import moment from 'moment-timezone';

import { type AtlasGqlReportContext, useGetReportTemplatesQuery } from 'types/atlas-graphql';
import { notEmpty } from 'utils/types';

import { type ReportTemplate, type ReportTemplatesForContext } from './types';

export const TemplateIdMap = {
  PROJECT_WORK_SUMMARY_V2: '5912defa-381e-477a-907d-73278dda788b',
  EXCEL_CUSTOM_VIEW: 'dd7f02ee-793c-4559-b253-9ab2d2106850',
};

export const VISIBLE_COLUMNS_REPORT_ARG_KEY = 'visibleColumns';
export const SORT_BY_REPORT_ARG_KEY = 'sortBy';
export const UTC_OFFSET_REPORT_ARG_KEY = 'utcOffset';
export const TIME_ZONE_REPORT_ARG_KEY = 'timeZone';

export const isExcelCustomViewTemplate = (templateId?: string | null): boolean =>
  templateId === TemplateIdMap.EXCEL_CUSTOM_VIEW;

// Marks the Advanced row. Focus moves to it when a pinned or recent row leaves the menu.
export const ADVANCED_MENU_ITEM_ATTRIBUTE = 'data-export-report-advanced';

// Inset so a menu nudged against the viewport edge does not sit flush with it.
const MENU_VIEWPORT_GAP_PX = 8;

// The widest the menu may be, as a share of the viewport width.
const MENU_MAX_WIDTH_VIEWPORT_FRACTION = 0.75;

export type ExportReportMenuBounds = {
  maxHeight: number;
  maxWidth: number;
};

/**
 * Caps the Export Report menu to the viewport. The height is the larger of the
 * space above and below the trigger, so the menu scrolls before it leaves the
 * window when the button is not at the top. The width is three quarters of the
 * viewport, so a long option label wraps well before the screen edge.
 */
export const getExportReportMenuBounds = (
  trigger: HTMLElement | null,
  viewport: { width: number; height: number } = {
    width: window.innerWidth,
    height: window.innerHeight,
  }
): ExportReportMenuBounds => {
  const maxWidth = viewport.width * MENU_MAX_WIDTH_VIEWPORT_FRACTION;
  if (!trigger) {
    return {
      maxHeight: Math.max(viewport.height - MENU_VIEWPORT_GAP_PX * 2, 0),
      maxWidth,
    };
  }
  const { top, bottom } = trigger.getBoundingClientRect();
  const room = Math.max(top, viewport.height - bottom) - MENU_VIEWPORT_GAP_PX;
  return {
    maxHeight: Math.max(room, 0),
    maxWidth,
  };
};

// getTimezoneOffset() is UTC minus local minutes. Negate it so the report
// service shifts datetimes into the user's timezone.
export const getUtcOffsetReportArg = (): { key: string; value: string } => ({
  key: UTC_OFFSET_REPORT_ARG_KEY,
  value: String(-1 * new Date().getTimezoneOffset()),
});

// The IANA zone the Damages table formats dates in, so the report service can apply
// each row's own DST offset instead of the offset at export time.
export const getTimeZoneReportArg = (): { key: string; value: string } => ({
  key: TIME_ZONE_REPORT_ARG_KEY,
  value: moment.tz.guess(),
});

// Picture report templates that support filtering the exported images by the
// active Navigator filters via the "Include selected pictures only" option.
export const PICTURE_REPORT_TEMPLATE_IDS = [
  'd6aa0b3f-5d2f-4012-8e60-259f07f35597',
  'e3fc9fb0-caca-42a5-b799-4cc11af2adf6',
  'e411c470-98bb-11ed-8846-c7f80bcd3d22',
  'db6c491d-4400-4512-9c9b-cf324b0ddfde',
];

type ReportArgument = { key: string; value: string };

// Top-level report arguments emitted only for "Include selected pictures only" picture
// reports (see `getPictureAreaArguments`). They must never reach a non-picture report.
// `getPictureAreaArguments` also injects a top-level `id`, but only into the INSPECTIONS
// context, which holds picture templates exclusively — so it never reaches the strip path
// for a non-picture report. It is deliberately omitted here because `id` is a legitimate
// base argument in other contexts and must be preserved for them.
export const PICTURE_REPORT_ONLY_ARG_KEYS = [
  'gpsBoundary',
  'fetchPicturesInArea',
  'inferenceFilters',
  'imageType',
];

// "Include selected pictures only" adds picture-scoped arguments to a context's shared
// report arguments, so they would otherwise be applied to every template in that context.
// They only make sense for picture reports, so this strips them from any other template
// selected in the same (mixed) export:
//  - image-filter entries (marked with `ignoreInQuery`) are removed from a merged
//    `filterBy`, and a `filterBy` left with no entries is dropped entirely;
//  - the top-level picture-only arguments (`PICTURE_REPORT_ONLY_ARG_KEYS`) are dropped.
export function stripPictureReportArguments(args: ReportArgument[]): ReportArgument[] {
  return args
    .filter((arg) => !PICTURE_REPORT_ONLY_ARG_KEYS.includes(arg.key))
    .map((arg) => {
      if (arg.key !== 'filterBy') {
        return arg;
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(arg.value);
      } catch {
        return arg;
      }
      if (!Array.isArray(parsed)) {
        return arg;
      }
      return {
        ...arg,
        value: JSON.stringify(parsed.filter((entry) => !entry?.ignoreInQuery)),
      };
    })
    .filter((arg) => {
      if (arg.key !== 'filterBy') {
        return true;
      }
      try {
        const parsed = JSON.parse(arg.value);
        // Only drop a `filterBy` we actually emptied (an array left with no entries).
        // Non-array shapes are preserved, matching the map stage above — a missing
        // `.length` must not be read as an empty array and discard the argument.
        return !Array.isArray(parsed) || parsed.length > 0;
      } catch {
        return true;
      }
    });
}

export function useGetReportTemplatesForContext(
  contexts: AtlasGqlReportContext[]
): ReportTemplatesForContext[] {
  const { data = {}, loading } = useGetReportTemplatesQuery({
    variables: { contexts },
  });

  const reportTemplates = data?.reportTemplates || [];

  return contexts.map((context) => {
    const templatesForContext = (reportTemplates || []).filter((t) => t.context === context);
    return {
      context,
      templates: templatesForContext.map((template) => ({
        template,
        disabled: undefined,
      })),
      loading,
    };
  });
}

/**
 * Helper function that sets the disabled flag on templates based on whether or not
 * the item count from the arguments is greater than the maximum defined in the template
 * @param contextTemplates An array of report template objects
 * @param reportArgInfoForContextLu An object mapping report arguements to a specific context name
 * @returns Updated templates, where the disabled flag has been properly set
 */
export function getAugmentedTemplates(contextTemplates: any[], reportArgInfoForContextLu: any) {
  return contextTemplates.map(({ context, templates, ...rest }) => {
    const reportArgsInfo = reportArgInfoForContextLu[context];
    // A template is disabled if any of the report arguments for the template's
    // context would yield an item count that is too big for the template.
    let nonNullItemCounts: number[] = [];
    if (reportArgsInfo?.reportArguments?.length) {
      nonNullItemCounts = reportArgsInfo.reportArguments
        .map(({ itemCount }: { itemCount: number }) => itemCount)
        .filter(notEmpty);
    }

    const maxItemCount: number = nonNullItemCounts.length ? Math.max(...nonNullItemCounts) : -1;

    const newTemplates: ReportTemplate[] = templates.map(({ template }: ReportTemplate) => {
      const { itemLimit } = template;
      return {
        disabled: reportArgsInfo.optionDisabled
          ? true
          : maxItemCount > 0 && itemLimit
            ? maxItemCount > itemLimit
            : false,
        template,
      };
    });
    return { context, templates: newTemplates, ...rest };
  });
}
